import { useCallback, useRef } from "react";
import {
  ICategorizedResponse,
  Id,
  ACTIVITIES_AUTOCOMPLETE_FOCUSED,
  ACTIVITIES_AUTOCOMPLETE_TYPED,
  ACTIVITIES_AUTOCOMPLETE_SUGGESTION_CLICKED,
  ActivitiesAutocompleteFocusedProperties,
  ActivitiesAutocompleteTypedProperties,
  ActivitiesAutocompleteSuggestionClickedProperties,
  ActivitiesAutocompleteSuggestionType,
} from "redmond";
import { trackEvent } from "../api/v0/analytics/trackEvent";
import { findFlatIndexInCategories } from "../utils/findFlatIndexInCategories";

interface UseAutocompleteAnalyticsParams {
  tripSuggestionsLoaded: boolean;
  tripDestinationsCount: number;
  locationCategories: ICategorizedResponse[] | undefined;
  hasNearbyResult: boolean;
  isSearchNearbyExperiment: boolean;
}

export const useAutocompleteAnalytics = ({
  tripSuggestionsLoaded,
  tripDestinationsCount,
  locationCategories,
  hasNearbyResult,
  isSearchNearbyExperiment,
}: UseAutocompleteAnalyticsParams) => {
  const hasFiredFocusEventRef = useRef(false);
  const hasFiredTypedEventRef = useRef(false);

  const resetAnalyticsRefs = useCallback(() => {
    hasFiredFocusEventRef.current = false;
    hasFiredTypedEventRef.current = false;
  }, []);

  const trackAutocompleteFocus = useCallback(() => {
    if (!hasFiredFocusEventRef.current) {
      hasFiredFocusEventRef.current = true;
      const properties: ActivitiesAutocompleteFocusedProperties = {
        trip_suggestions_loaded: tripSuggestionsLoaded,
      };
      trackEvent({
        eventName: ACTIVITIES_AUTOCOMPLETE_FOCUSED,
        properties,
      }).catch(() => {});
    }
  }, [tripSuggestionsLoaded]);

  const trackAutocompleteTyped = useCallback(() => {
    if (!hasFiredTypedEventRef.current) {
      hasFiredTypedEventRef.current = true;
      const properties: ActivitiesAutocompleteTypedProperties = {
        trip_suggestions_loaded: tripSuggestionsLoaded,
      };
      trackEvent({
        eventName: ACTIVITIES_AUTOCOMPLETE_TYPED,
        properties,
      }).catch(() => {});
    }
  }, [tripSuggestionsLoaded]);

  const trackTripClick = useCallback(
    (index: number) => {
      const properties: ActivitiesAutocompleteSuggestionClickedProperties = {
        index,
        suggestion_type: ActivitiesAutocompleteSuggestionType.Trip,
        trip_suggestions_loaded: tripSuggestionsLoaded,
      };
      trackEvent({
        eventName: ACTIVITIES_AUTOCOMPLETE_SUGGESTION_CLICKED,
        properties,
      }).catch(() => {});
    },
    [tripSuggestionsLoaded]
  );

  const trackRecentSearchClick = useCallback(
    (searchIndex: number) => {
      const nearbyOffset =
        isSearchNearbyExperiment && hasNearbyResult ? 1 : 0;
      const properties: ActivitiesAutocompleteSuggestionClickedProperties = {
        index: tripDestinationsCount + nearbyOffset + searchIndex,
        suggestion_type: ActivitiesAutocompleteSuggestionType.RecentSearch,
        trip_suggestions_loaded: tripSuggestionsLoaded,
      };
      trackEvent({
        eventName: ACTIVITIES_AUTOCOMPLETE_SUGGESTION_CLICKED,
        properties,
      }).catch(() => {});
    },
    [
      tripDestinationsCount,
      isSearchNearbyExperiment,
      hasNearbyResult,
      tripSuggestionsLoaded,
    ]
  );

  const trackLocationSelected = useCallback(
    (locationId: Id) => {
      const index = locationCategories
        ? findFlatIndexInCategories(locationCategories, locationId)
        : 0;
      const properties: ActivitiesAutocompleteSuggestionClickedProperties = {
        index,
        suggestion_type: ActivitiesAutocompleteSuggestionType.Search,
        trip_suggestions_loaded: tripSuggestionsLoaded,
      };
      trackEvent({
        eventName: ACTIVITIES_AUTOCOMPLETE_SUGGESTION_CLICKED,
        properties,
      }).catch(() => {});
    },
    [locationCategories, tripSuggestionsLoaded]
  );

  const trackSearchNearbyClick = useCallback(() => {
    const properties: ActivitiesAutocompleteSuggestionClickedProperties = {
      index: tripDestinationsCount,
      suggestion_type: ActivitiesAutocompleteSuggestionType.Nearby,
      trip_suggestions_loaded: tripSuggestionsLoaded,
    };
    trackEvent({
      eventName: ACTIVITIES_AUTOCOMPLETE_SUGGESTION_CLICKED,
      properties,
    }).catch(() => {});
  }, [tripDestinationsCount, tripSuggestionsLoaded]);

  return {
    resetAnalyticsRefs,
    trackAutocompleteFocus,
    trackAutocompleteTyped,
    trackTripClick,
    trackRecentSearchClick,
    trackLocationSelected,
    trackSearchNearbyClick,
  };
};
